/**
 * PURPOSE: "Agregar deportista" a una prueba de Atletismo: inscripción en la categoría + lista de salida de una serie.
 * RESPONSIBILITIES: Buscar entre los acreditados de atletismo del evento (nombre, documento o sigla), mostrar sigla, rama y
 *   su situación (ya está en la prueba, inscripción vigente / retirada / nueva, no elegible), avisar si la rama o el nivel no
 *   coinciden con los de la prueba (hay que confirmarlo) y pedir lo que falta: la serie (si la prueba tiene varias) y, en
 *   pista, el carril (800/1500 admiten dos por carril); con el carril ocupado, "Reemplazar a X" o "Intercambiar" (LanePicker).
 *   `preset` ({ roundId, lane, mode, targetRowId }) abre la ventana ya en esa serie y carril: lo usa "Reemplazar a X" de la
 *   fila y el "+" de un carril libre. En concursos se agrega al final del orden de salida.
 *   El servidor valida todo otra vez y nunca crea personas ni acreditaciones; cualquier rechazo se muestra aquí, con su motivo.
 * COLLABORATORS: PruebaDetailPanel.jsx, CategoriasIndividualesArea.jsx, placement.js, services/individualEntriesService.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/AddAthleteModal.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import Button from "@mui/joy/Button";
import { FiAlertTriangle, FiSearch, FiUserPlus } from "react-icons/fi";

import {
  INELIGIBLE_LABELS,
  branchLabel,
  isRoundLocked,
  levelLabel,
  nextOrderOf,
  occupantLabel,
  occupantsOfLane,
  resolveLaneChoice,
  roundName,
  usesLanes,
} from "../placement.js";
import LanePicker, { EMPTY_LANE_CHOICE } from "./LanePicker.jsx";
import { Badge, Spinner, selectClass } from "./ui.jsx";

const MIN_SEARCH = 2;

const inscriptionBadge = (candidate) => {
  if (candidate.alreadyInPrueba) return <Badge tone="green">Ya está en la prueba</Badge>;
  if (candidate.inscription === "active") return <Badge tone="blue">Inscrito · sin serie</Badge>;
  if (candidate.inscription === "withdrawn") return <Badge tone="amber">Inscripción retirada · se reactiva</Badge>;
  return <Badge tone="slate">Se inscribe en la categoría</Badge>;
};

const AddAthleteModal = ({ open, prueba, rounds, defaultRoundId, preset = null, searchCandidates, onSubmit, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [selected, setSelected] = useState(null);
  const [roundId, setRoundId] = useState(null);
  const [lane, setLane] = useState("");
  const [laneChoice, setLaneChoice] = useState(EMPTY_LANE_CHOICE);
  const [confirmWarnings, setConfirmWarnings] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const roundList = useMemo(() => (Array.isArray(rounds) ? rounds : []), [rounds]);
  const laneMode = usesLanes(prueba);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setResults(null);
    setSearchError("");
    setSelected(null);
    setLane(preset?.lane ? String(preset.lane) : "");
    setLaneChoice(preset?.mode ? { mode: preset.mode, targetRowId: preset.targetRowId ?? null, confirmResult: false } : EMPTY_LANE_CHOICE);
    setConfirmWarnings(false);
    setSubmitting(false);
    setError("");
    const wanted = preset?.roundId ?? defaultRoundId;
    const initial = roundList.find((round) => round.id === wanted && !isRoundLocked(round)) || roundList.find((round) => !isRoundLocked(round)) || roundList[0];
    setRoundId(initial ? initial.id : null);
    // Solo al abrir: los cambios de `rounds` mientras está abierto no deben borrar lo que el operador eligió.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const text = query.trim();
    if (text.length < MIN_SEARCH) {
      setResults(null);
      setSearchError("");
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      setSearchError("");
      try {
        const candidates = await searchCandidates(text);
        if (!cancelled) setResults(candidates);
      } catch (failure) {
        if (!cancelled) {
          setResults(null);
          setSearchError(failure?.response?.data?.message || failure?.message || "No se pudo buscar.");
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, query, searchCandidates]);

  const round = roundList.find((item) => item.id === roundId) || null;
  const locked = isRoundLocked(round);
  const warnings = [];
  if (selected?.branchMismatch) {
    warnings.push(`La prueba es de ${branchLabel(prueba?.branch?.code)} y el deportista figura como ${branchLabel(selected.branch)}.`);
  }
  if (selected?.levelMismatch) {
    warnings.push(
      `Sus inscripciones son de nivel ${selected.levels.map(levelLabel).join(" / ")} y la prueba es de nivel ${prueba?.level?.name || levelLabel(prueba?.level?.code)}.`
    );
  }
  const needsLane = laneMode;
  const laneState = resolveLaneChoice({ prueba, round, lane: Number(lane) || null, choice: laneChoice, inCombined: Boolean(prueba?.parentId) });
  const replacing = preset?.mode === "replace" && preset?.targetRowId ? occupantsOfLane(round, preset.lane).find((item) => item.id === preset.targetRowId) : null;
  const missing = !selected
    ? "Elige al deportista."
    : !round
      ? "Elige la serie."
      : locked
        ? `${roundName(round)} está terminada (oficial): reábrela para agregar deportistas.`
        : needsLane && !lane
          ? "Elige el carril."
          : laneState.missing
            ? laneState.missing
            : warnings.length && !confirmWarnings
            ? "Confirma el aviso para continuar."
            : "";

  const submit = async () => {
    if (missing || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const outcome = await onSubmit({
        idacreditation: selected.idacreditation,
        roundId: round.id,
        lane: needsLane ? Number(lane) : null,
        ...laneState.params,
        candidate: selected,
        round,
      });
      if (outcome?.ok) onClose();
      else if (outcome?.close) onClose();
      else setError(outcome?.message || "No se pudo agregar.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={Boolean(open)} onClose={submitting ? undefined : onClose}>
      <ModalDialog sx={{ width: "min(96vw, 680px)", maxHeight: "92vh", borderRadius: "16px", overflow: "auto" }}>
        <ModalClose disabled={submitting} />
        <DialogTitle>
          {replacing ? `Reemplazar a ${occupantLabel(replacing)}` : "Agregar deportista"} · {prueba?.label || prueba?.name}
        </DialogTitle>
        <DialogContent>
          <p className="text-xs leading-5 text-slate-500">
            Busca entre los acreditados de atletismo. Si no está inscrito en la categoría se inscribe y queda en la lista de
            salida (planilla, FinishLynx y Hayllis). No se crean personas ni acreditaciones nuevas.
          </p>

          {!selected ? (
            <>
              <label className="mt-3 flex items-center gap-2 rounded-lg border border-slate-300 px-2 py-1.5 focus-within:border-indigo-400">
                <FiSearch className="shrink-0 text-slate-400" aria-hidden="true" />
                <input
                  type="search"
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Nombre, DNI o sigla (mín. 2 caracteres)"
                  aria-label="Buscar deportista"
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                />
                {searching ? <Spinner /> : null}
              </label>
              {searchError ? (
                <p role="alert" className="mt-2 text-xs font-semibold text-rose-700">
                  {searchError}
                </p>
              ) : null}
              {Array.isArray(results) ? (
                results.length ? (
                  <ul className="mt-2 max-h-72 space-y-1 overflow-y-auto" aria-label="Resultados de la búsqueda">
                    {results.map((candidate) => {
                      const reason = candidate.eligible ? null : INELIGIBLE_LABELS[candidate.ineligibleReason] || "No elegible";
                      const blocked = candidate.alreadyInPrueba || !candidate.eligible;
                      return (
                        <li key={candidate.idacreditation} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-800" title={candidate.name}>
                              {candidate.name}
                            </p>
                            <p className="flex flex-wrap items-center gap-1 text-[11px] font-bold text-slate-500">
                              <span title={candidate.institution?.name}>{candidate.institution?.abrev || candidate.institution?.name}</span>
                              <span>· {branchLabel(candidate.branch)}</span>
                              {candidate.document ? <span>· {candidate.document}</span> : null}
                              {reason ? <span className="text-rose-600">· {reason}</span> : null}
                            </p>
                            <div className="mt-0.5 flex flex-wrap gap-1">
                              {inscriptionBadge(candidate)}
                              {candidate.branchMismatch ? <Badge tone="amber">Rama distinta</Badge> : null}
                              {candidate.levelMismatch ? <Badge tone="amber">Nivel distinto</Badge> : null}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelected(candidate);
                              setConfirmWarnings(false);
                              setError("");
                            }}
                            disabled={blocked}
                            aria-label={`Elegir a ${candidate.name}`}
                            className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-indigo-50 px-2 py-1 text-[11px] font-bold text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <FiUserPlus aria-hidden="true" /> Elegir
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="mt-2 text-xs italic text-slate-500">Ningún acreditado de atletismo coincide.</p>
                )
              ) : null}
            </>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-black text-slate-800">{selected.name}</p>
                  <p className="text-[11px] font-bold text-slate-500">
                    {selected.institution?.abrev} · {branchLabel(selected.branch)}
                    {selected.document ? ` · ${selected.document}` : ""}
                  </p>
                  <div className="mt-0.5">{inscriptionBadge(selected)}</div>
                </div>
                <button type="button" onClick={() => setSelected(null)} disabled={submitting} className="text-xs font-bold text-indigo-700 underline">
                  Cambiar
                </button>
              </div>

              {roundList.length > 1 ? (
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Serie</span>
                  <select
                    value={roundId ?? ""}
                    onChange={(event) => {
                      setRoundId(Number(event.target.value));
                      setLane("");
                      setLaneChoice(EMPTY_LANE_CHOICE);
                    }}
                    className={selectClass}
                    aria-label="Serie de destino"
                  >
                    {roundList.map((item) => (
                      <option key={item.id} value={item.id} disabled={isRoundLocked(item)}>
                        {roundName(item)}
                        {isRoundLocked(item) ? " (terminada)" : ""}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {needsLane ? (
                <LanePicker
                  prueba={prueba}
                  round={round}
                  lane={lane}
                  onLaneChange={setLane}
                  choice={laneChoice}
                  onChoiceChange={setLaneChoice}
                  inCombined={Boolean(prueba?.parentId)}
                />
              ) : (
                <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                  Es una prueba de campo: sin carril. Se agrega al final del orden de salida (N.º {nextOrderOf(round)}).
                </p>
              )}

              {warnings.length ? (
                <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  {warnings.map((text) => (
                    <p key={text} className="flex items-start gap-2 text-xs font-semibold text-amber-800">
                      <FiAlertTriangle className="mt-0.5 shrink-0" aria-hidden="true" /> {text}
                    </p>
                  ))}
                  <label className="mt-2 flex items-center gap-2 text-xs font-bold text-amber-900">
                    <input type="checkbox" checked={confirmWarnings} onChange={(event) => setConfirmWarnings(event.target.checked)} />
                    Confirmo que corresponde agregarlo a esta prueba.
                  </label>
                </div>
              ) : null}
            </div>
          )}

          {error ? (
            <p role="alert" className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700">
              {error}
            </p>
          ) : null}
          {selected && missing ? <p className="mt-2 text-xs font-semibold text-slate-500">{missing}</p> : null}
        </DialogContent>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="plain" color="neutral" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="solid" color="primary" onClick={submit} disabled={Boolean(missing) || submitting} startDecorator={submitting ? <Spinner /> : null}>
            {submitting ? "Agregando..." : "Agregar a la prueba"}
          </Button>
        </div>
      </ModalDialog>
    </Modal>
  );
};

export default AddAthleteModal;
